import { fetchUtils } from "react-admin";
import type { DataProvider, Identifier } from "ra-core";
import { stringify } from "query-string";
import { unflatten } from "flat";
import { getApiUrl } from "@/constants";
import { razaEventListDateQuery } from "@/containers/raza/raza-events/list/raza-event-date-scope";
import httpClient from "./http-client";
import { normalizePermissionsWithChoices, type PermissionChoice } from "@/utils/permission-utils";

const convertRows = (rows: unknown[]) => rows.map((row) => unflatten(row as object));

/** Nest `/raza-events` controller base path (hyphenated). Resource name in react-admin. */
const RAZA_EVENTS_API = "raza-events";

let availablePermissionsCache: PermissionChoice[] | null = null;
let availablePermissionsPromise: Promise<PermissionChoice[]> | null = null;

export const clearAvailablePermissionsCache = (): void => {
  availablePermissionsCache = null;
  availablePermissionsPromise = null;
};

if (typeof window !== "undefined") {
  window.addEventListener("permissionsCacheCleared", () => {
    clearAvailablePermissionsCache();
  });
}

const getAvailablePermissions = async (): Promise<PermissionChoice[]> => {
  if (availablePermissionsCache) {
    return availablePermissionsCache;
  }

  if (availablePermissionsPromise) {
    return availablePermissionsPromise;
  }

  availablePermissionsPromise = httpClient(`${getApiUrl()}/admins/permissions/available`, {
    method: "GET",
  })
    .then(({ json }) => {
      const body = json as { rows?: PermissionChoice[]; data?: PermissionChoice[] };
      availablePermissionsCache = body.rows ?? body.data ?? [];
      availablePermissionsPromise = null;
      return availablePermissionsCache;
    })
    .catch((error: unknown) => {
      console.error("Failed to fetch permissions for normalization:", error);
      availablePermissionsPromise = null;
      return [];
    });

  return availablePermissionsPromise;
};

const normalizeAdminPermissions = async (
  data: Record<string, unknown>
): Promise<Record<string, unknown>> => {
  const permissions = data.permissions;
  if (!permissions || !Array.isArray(permissions)) {
    return data;
  }

  const availableChoices = await getAvailablePermissions();

  if (!availableChoices || availableChoices.length === 0) {
    return data;
  }

  const normalized = normalizePermissionsWithChoices(permissions as string[], availableChoices);

  return {
    ...data,
    permissions: normalized,
  };
};

const dataProvider = {
  getList: (resource: string, params: Parameters<DataProvider["getList"]>[1]) => {
    if (resource === "razaEventsMissingSadarat") {
      const pagination = params.pagination ?? { page: 1, perPage: 50 };
      const dateScope = params?.meta?.dateScope === "past" ? "past" : "upcoming";
      const scopeQuery = razaEventListDateQuery(dateScope);
      const filterGender =
        params.filter?.missingSadaratGender === "male" ||
        params.filter?.missingSadaratGender === "female"
          ? params.filter.missingSadaratGender
          : undefined;
      const { missingSadaratGender: _dropGender, ...restFilter } = (params.filter ?? {}) as Record<
        string,
        unknown
      > & { missingSadaratGender?: unknown };
      const filter = {
        missingSadarat: true,
        ...(scopeQuery.start ? { start: scopeQuery.start } : {}),
        ...(scopeQuery.end ? { end: scopeQuery.end } : {}),
        ...restFilter,
        ...(filterGender ? { missingSadaratGender: filterGender } : {}),
      };
      const sort = params.sort ?? scopeQuery.sort;
      const page = pagination.page ?? 1;
      const perPage = pagination.perPage ?? 50;
      const query = {
        ...fetchUtils.flattenObject(filter),
        orderBy: sort.field,
        order: sort.order,
        limit: perPage,
        startAfter: (page - 1) * perPage,
      };
      const url = `${getApiUrl()}/${RAZA_EVENTS_API}?${stringify(query)}`;
      return httpClient(url).then(({ json }) => {
        const body = json as { count?: number; rows?: unknown[] };
        const rows = body.rows ?? [];
        return {
          data: convertRows(rows),
          total: body.count ?? rows.length,
        };
      });
    }

    if (resource === "razaEventsUpcoming") {
      const scope = params?.meta?.attendanceScope === "past" ? "past" : "upcoming";
      const path = scope === "past" ? "past" : "upcoming";
      return httpClient(`${getApiUrl()}/${RAZA_EVENTS_API}/${path}`).then(({ json }) => {
        const body = json as { count?: number; rows?: unknown[] };
        const rows = body.rows ?? [];
        return {
          data: convertRows(rows),
          total: body.count ?? rows.length,
        };
      });
    }

    if (resource === "razaEventAttendances") {
      const pagination = params.pagination ?? { page: 1, perPage: 10 };
      const filter = params.filter ?? {};
      const sort = params.sort ?? { field: "id", order: "DESC" as const };
      const page = pagination.page ?? 1;
      const perPage = pagination.perPage ?? 10;
      const query = {
        ...fetchUtils.flattenObject(filter),
        orderBy: sort.field,
        order: sort.order,
        limit: perPage,
        startAfter: (page - 1) * perPage,
      };
      const url = `${getApiUrl()}/${RAZA_EVENTS_API}/attendances?${stringify(query)}`;
      return httpClient(url).then(({ json }) => {
        const body = json as { count?: number; rows?: unknown[] };
        const rows = body.rows ?? [];
        return {
          data: convertRows(rows),
          total: body.count ?? rows.length,
        };
      });
    }

    if (resource === "itsdataAddressChangeQueue") {
      const pagination = params.pagination ?? { page: 1, perPage: 10 };
      const filter = params.filter ?? {};
      const sort = params.sort ?? { field: "id", order: "DESC" as const };
      const page = pagination.page ?? 1;
      const perPage = pagination.perPage ?? 10;
      const query = {
        ...fetchUtils.flattenObject(filter),
        orderBy: sort.field,
        order: sort.order,
        limit: perPage,
        startAfter: (page - 1) * perPage,
      };
      const url = `${getApiUrl()}/itsdata/address-change-queue?${stringify(query)}`;
      return httpClient(url).then(({ json }) => {
        const body = json as { count?: number; rows?: unknown[] };
        const rows = body.rows ?? [];
        return {
          data: convertRows(rows),
          total: body.count,
        };
      });
    }

    if (resource === "niyaazPirsa") {
      const pagination = params.pagination ?? { page: 1, perPage: 10 };
      const filter = { pirsa_gt: 0, ...(params.filter ?? {}) };
      const sort = params.sort ?? { field: "markaz", order: "ASC" as const };
      const page = pagination.page ?? 1;
      const perPage = pagination.perPage ?? 10;
      const query = {
        ...fetchUtils.flattenObject(filter),
        orderBy: sort.field,
        order: sort.order,
        limit: perPage,
        startAfter: (page - 1) * perPage,
      };
      const url = `${getApiUrl("niyaaz")}/niyaaz?${stringify(query)}`;
      return httpClient(url).then(({ json }) => {
        const body = json as { count?: number; rows?: unknown[]; counts?: unknown };
        localStorage.setItem("niyaazPirsa_count", JSON.stringify(body.counts));
        return {
          data: convertRows(body.rows ?? []),
          total: body.count,
        };
      });
    }

    if (resource === "fmbThaliDistributionDailyRun") {
      const filter = params.filter ?? {};
      const flat = fetchUtils.flattenObject(filter) as Record<string, unknown>;
      const rawDate = flat.date;
      let dateStr = "";
      if (typeof rawDate === "string") {
        dateStr = rawDate.trim();
      } else if (rawDate instanceof Date && !Number.isNaN(rawDate.getTime())) {
        dateStr = rawDate.toISOString().slice(0, 10);
      }
      const date = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
        ? dateStr
        : new Date().toISOString().slice(0, 10);
      const sort = params.sort ?? { field: "code", order: "ASC" as const };
      const qRaw = flat.q;
      const searchQ = typeof qRaw === "string" ? qRaw.trim().toLowerCase() : "";
      const url = `${getApiUrl()}/fmbThaliDistribution/dashboard?${stringify({ date })}`;
      return httpClient(url).then(({ json }) => {
        const body = json as {
          count?: number;
          rows?: Array<
            Record<string, unknown> & {
              distributorId?: string;
              code?: string;
              name?: string;
              total?: number;
              thaliTypeCounts?: Array<{ name?: string; count?: number }>;
            }
          >;
          hasDailyMenu?: boolean;
          date?: string;
          timezone?: string;
        };
        let rows = body.rows ?? [];
        if (searchQ) {
          rows = rows.filter((row) => {
            const code = String(row.code ?? "").toLowerCase();
            const name = String(row.name ?? "").toLowerCase();
            const breakdown = (row.thaliTypeCounts ?? [])
              .map((x) => `${x.name ?? ""} ${x.count ?? ""}`)
              .join(" ")
              .toLowerCase();
            return code.includes(searchQ) || name.includes(searchQ) || breakdown.includes(searchQ);
          });
        }
        const sortField = typeof sort.field === "string" ? sort.field : "code";
        const desc = String(sort.order ?? "ASC").toUpperCase() === "DESC";
        rows = [...rows].sort((a, b) => {
          let va: string | number;
          let vb: string | number;
          if (sortField === "name") {
            va = String(a.name ?? "");
            vb = String(b.name ?? "");
          } else if (sortField === "total") {
            va = Number(a.total ?? 0);
            vb = Number(b.total ?? 0);
          } else {
            va = String(a.code ?? "");
            vb = String(b.code ?? "");
          }
          if (typeof va === "number" && typeof vb === "number") {
            const n = va - vb;
            return desc ? -n : n;
          }
          const cmp = String(va).localeCompare(String(vb), undefined, {
            numeric: true,
            sensitivity: "base",
          });
          return desc ? -cmp : cmp;
        });
        const withIds = rows.map((row) => ({
          ...row,
          id: String(row.distributorId ?? ""),
        }));
        return {
          data: convertRows(withIds),
          total: withIds.length,
          meta: {
            hasDailyMenu: body.hasDailyMenu !== false,
            dashboardDate: body.date ?? date,
            timezone: body.timezone,
          },
        };
      });
    }

    const pagination = params.pagination ?? { page: 1, perPage: 10 };
    const filter = params.filter ?? {};
    const sort = params.sort ?? { field: "id", order: "DESC" as const };
    const page = pagination.page ?? 1;
    const perPage = pagination.perPage ?? 10;
    const query = {
      ...fetchUtils.flattenObject(filter),
      orderBy: sort.field,
      order: sort.order,
      limit: perPage,
      startAfter: (page - 1) * perPage,
    };
    const url = `${getApiUrl(resource)}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({ json }) => {
      const body = json as { count?: number; rows?: unknown[]; counts?: unknown };
      const broadcastId = (filter as Record<string, unknown>)?.broadcastId;
      const storageKey = broadcastId ? `${resource}_${broadcastId}_count` : `${resource}_count`;
      if (body.counts) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(body.counts));
        } catch {
          // ignore storage quota errors
        }
      }
      return {
        data: convertRows(body.rows ?? []),
        total: body.count,
        meta: { counts: body.counts },
      };
    });
  },

  getOne: (resource: string, params: Parameters<DataProvider["getOne"]>[1]) => {
    if (resource === "razaEventAttendances") {
      return httpClient(`${getApiUrl()}/${RAZA_EVENTS_API}/attendances/${params.id}`).then(
        ({ json }) => {
          const body = json as { rows?: Record<string, unknown>[] };
          const row = body.rows![0];
          return { data: row };
        }
      );
    }
    return httpClient(`${getApiUrl(resource)}/${resource}/${params.id}`).then(({ json }) => {
      const body = json as { rows?: Record<string, unknown>[] };
      const row = body.rows![0];
      if (resource === "fmbDailyMenu" && row && Array.isArray(row.dishIds)) {
        const dishIds = row.dishIds as string[];
        return {
          data: {
            ...row,
            menuLines: dishIds.map((dishId) => ({ dishId })),
          },
        };
      }
      return { data: row };
    });
  },

  getMany: (resource: string, params: Parameters<DataProvider["getMany"]>[1]) => {
    const ids = (params.ids ?? []).map((id) => (id == null ? "" : String(id))).filter(Boolean);

    if (resource === "itsdata" && ids.length > 0) {
      const query = {
        filter: JSON.stringify({ id: ids }),
        limit: Math.min(Math.max(ids.length, 50), 100),
      };
      const url = `${getApiUrl(resource)}/${resource}?${stringify(query)}`;
      return httpClient(url).then(({ json }) => {
        const body = json as { rows?: unknown[] };
        const rows = body.rows ?? [];
        return {
          data: convertRows(rows),
          total: rows.length,
        };
      });
    }

    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    const url = `${getApiUrl(resource)}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({ json }) => {
      const body = json as { count?: number; rows?: unknown[] };
      return {
        data: body.rows ?? [],
        total: body.count,
      };
    });
  },

  getManyReference: (resource: string, params: Parameters<DataProvider["getManyReference"]>[1]) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    if (resource === "razaEventAttendances") {
      const query = {
        ...fetchUtils.flattenObject(params.filter),
        [params.target]: params.id,
        orderBy: field,
        order,
        limit: perPage,
        startAfter: (page - 1) * perPage,
      };
      const url = `${getApiUrl()}/${RAZA_EVENTS_API}/attendances?${stringify(query)}`;
      return httpClient(url).then(({ json }) => {
        const body = json as { rows?: unknown[]; count?: number };
        return {
          data: convertRows(body.rows ?? []),
          total: body.count,
        };
      });
    }

    const query = {
      ...fetchUtils.flattenObject(params.filter),
      [params.target]: params.id,
      orderBy: field,
      order,
      limit: perPage,
      startAfter: (page - 1) * perPage,
    };
    const url = `${getApiUrl(resource)}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({ json }) => {
      const body = json as { rows?: unknown[]; count?: number };
      return {
        data: body.rows ?? [],
        total: body.count,
      };
    });
  },

  update: async (resource: string, params: Parameters<DataProvider["update"]>[1]) => {
    let dataToSend: Record<string, unknown> = { ...params.data } as Record<string, unknown>;

    if (resource === "admins" && dataToSend.permissions) {
      dataToSend = await normalizeAdminPermissions(dataToSend);
    }

    if (resource === "raza-events") {
      return httpClient(`${getApiUrl()}/${RAZA_EVENTS_API}/${params.id}`, {
        method: "PATCH",
        body: JSON.stringify(dataToSend),
      }).then(({ json }) => {
        const body = json as {
          rows?: Record<string, unknown>[];
          warnings?: string[];
        };
        const row = body.rows![0] ?? {};
        return {
          data: {
            ...row,
            ...(Array.isArray(body.warnings) && body.warnings.length > 0
              ? { warnings: body.warnings }
              : {}),
          },
        };
      });
    }

    if (resource === "itsdataAddressChangeQueue" && dataToSend.markDone) {
      return httpClient(`${getApiUrl()}/itsdata/address-change-queue/${params.id}/done`, {
        method: "PATCH",
        body: "{}",
      }).then(({ json }) => {
        const body = json as { rows?: unknown[] };
        const rows = body.rows ?? [];
        const converted = convertRows(rows);
        return { data: converted[0] ?? rows[0] };
      });
    }

    return httpClient(`${getApiUrl(resource)}/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(dataToSend),
    }).then(({ json }) => {
      const body = json as { rows?: Record<string, unknown>[] };
      return { data: body.rows![0] };
    });
  },

  updateMany: (resource: string, params: Parameters<DataProvider["updateMany"]>[1]) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return httpClient(`${getApiUrl(resource)}/${resource}?${stringify(query)}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: (json as { data?: Identifier[] }).data }));
  },

  create: async (resource: string, params: Parameters<DataProvider["create"]>[1]) => {
    let dataToSend: Record<string, unknown> = { ...params.data } as Record<string, unknown>;

    if (resource === "admins" && dataToSend.permissions) {
      dataToSend = await normalizeAdminPermissions(dataToSend);
    }

    if (resource === "razaEventAttendances") {
      const meta = params.meta as { razaEventId?: string } | undefined;
      const fromMeta = meta?.razaEventId;
      const fromData = dataToSend.razaEventId as string | undefined;
      const razaEventId = (
        typeof fromMeta === "string" && fromMeta.trim() ? fromMeta : fromData
      )?.trim();
      if (!razaEventId) {
        throw new Error(
          "razaEventAttendances create requires meta.razaEventId or data.razaEventId"
        );
      }
      const payload = { attendeeIts: dataToSend.attendeeIts };
      return httpClient(`${getApiUrl()}/${RAZA_EVENTS_API}/${razaEventId}/attendances`, {
        method: "POST",
        body: JSON.stringify(payload),
      }).then(({ json }) => {
        const body = json as { rows?: Record<string, unknown>[] };
        return { data: body.rows![0] };
      });
    }

    if (resource === "raza-events") {
      return httpClient(`${getApiUrl()}/${RAZA_EVENTS_API}`, {
        method: "POST",
        body: JSON.stringify(dataToSend),
      }).then(({ json }) => {
        const body = json as {
          rows?: Record<string, unknown>[];
          warnings?: string[];
        };
        const row = body.rows![0] ?? {};
        return {
          data: {
            ...row,
            ...(Array.isArray(body.warnings) && body.warnings.length > 0
              ? { warnings: body.warnings }
              : {}),
          },
        };
      });
    }

    return httpClient(`${getApiUrl(resource)}/${resource}`, {
      method: "POST",
      body: JSON.stringify(dataToSend),
    }).then(({ json }) => {
      const body = json as { rows?: Record<string, unknown>[] };
      return { data: body.rows![0] };
    });
  },

  createMany: (resource: string, params: { data?: unknown }) =>
    httpClient(`${getApiUrl(resource)}/${resource}/bulk-upload`, {
      method: "POST",
      body: JSON.stringify(params.data),
    }).then(({ json }) => {
      const body = json as { data?: unknown };
      return { data: body.data };
    }),

  delete: (resource: string, params: Parameters<DataProvider["delete"]>[1]) => {
    if (resource === "razaEventAttendances") {
      const prev = params.previousData as { razaEventId?: string } | undefined;
      const eventId = prev?.razaEventId;
      if (!eventId) {
        throw new Error("razaEventAttendances delete requires previousData.razaEventId");
      }
      return httpClient(`${getApiUrl()}/${RAZA_EVENTS_API}/${eventId}/attendances/${params.id}`, {
        method: "DELETE",
      }).then(({ json }) => {
        const body = json as { rows?: Record<string, unknown>[] };
        return { data: body.rows![0] };
      });
    }

    const auditRemark = (params.meta as { auditRemark?: string } | undefined)?.auditRemark;
    const deleteOptions =
      resource === "sabilReceipt" && auditRemark
        ? {
            method: "DELETE" as const,
            body: JSON.stringify({ auditRemark }),
          }
        : { method: "DELETE" as const };

    return httpClient(`${getApiUrl(resource)}/${resource}/${params.id}`, deleteOptions).then(
      ({ json }) => {
        const body = json as { rows?: Record<string, unknown>[] };
        return { data: body.rows![0] };
      }
    );
  },

  deleteMany: (
    resource: string,
    params: Parameters<DataProvider["deleteMany"]>[1] & { data?: unknown }
  ) =>
    Promise.all(
      params.ids.map((id) =>
        httpClient(`${getApiUrl(resource)}/${resource}/${id}`, {
          method: "DELETE",
          body: JSON.stringify(params.data),
        })
      )
    ).then(() => ({
      data: [] as Identifier[],
    })),

  deleteImage: (resource: string, params: Record<string, unknown>) =>
    httpClient(`${getApiUrl(resource)}/${resource}/delete-image?${stringify(params)}`).then(() => ({
      data: "",
    })),

  pdfDownload: (resource: string, params: { ids?: Identifier[]; name?: string }) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return httpClient(`${getApiUrl(resource)}/${resource}/${params.name}?${stringify(query)}`).then(
      (response) => {
        const linkSource = `data:application/pdf;base64,${response.body}`;
        const downloadLink = document.createElement("a");
        const fileName = `${params.name}-${Date.now()}.pdf`;
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
        return { data: [] };
      }
    );
  },

  previewRecipients: (
    resource: string,
    params: { filterCriteria?: unknown; limit?: number; offset?: number }
  ) => {
    const { filterCriteria, limit = 25, offset = 0 } = params;
    return httpClient(`${getApiUrl(resource)}/${resource}/preview-recipients`, {
      method: "POST",
      body: JSON.stringify({
        filterCriteria,
        limit,
        offset,
      }),
    }).then(({ json }) => {
      const body = json as {
        recipients?: unknown[];
        count?: number;
        fields?: unknown[];
      };
      const recipients = body.recipients ?? [];
      return {
        data: convertRows(recipients),
        total: body.count ?? 0,
        fields: body.fields ?? [],
      };
    });
  },

  previewMiqaatRsvpRecipients: (
    resource: string,
    params: {
      miqaatId: string;
      filterCriteria?: unknown;
      recipientItsIds?: string[];
      recipientPhoneNumbers?: string[] | string;
      limit?: number;
      offset?: number;
    }
  ) => {
    const {
      miqaatId,
      filterCriteria,
      recipientItsIds,
      recipientPhoneNumbers,
      limit = 10000,
      offset = 0,
    } = params;
    return httpClient(`${getApiUrl(resource)}/${resource}/preview-miqaat-rsvp-recipients`, {
      method: "POST",
      body: JSON.stringify({
        miqaatId,
        filterCriteria,
        recipientItsIds,
        recipientPhoneNumbers,
        limit,
        offset,
      }),
    }).then(({ json }) => {
      const body = json as {
        recipients?: unknown[];
        outboundCount?: number;
        fields?: unknown[];
        matchedItsIds?: string[];
        unmatchedItsIds?: string[];
      };
      const recipients = body.recipients ?? [];
      return {
        data: convertRows(recipients),
        total: body.outboundCount ?? 0,
        fields: body.fields ?? [],
        matchedItsIds: body.matchedItsIds ?? [],
        unmatchedItsIds: body.unmatchedItsIds ?? [],
      };
    });
  },

  previewEventRsvpRecipients: (
    resource: string,
    params: {
      eventId: string;
      filterCriteria?: unknown;
      limit?: number;
      offset?: number;
    }
  ) => {
    const { eventId, filterCriteria, limit = 10000, offset = 0 } = params;
    return httpClient(`${getApiUrl(resource)}/${resource}/preview-event-rsvp-recipients`, {
      method: "POST",
      body: JSON.stringify({ eventId, filterCriteria, limit, offset }),
    }).then(({ json }) => {
      const body = json as {
        recipients?: unknown[];
        outboundCount?: number;
        fields?: unknown[];
      };
      const recipients = body.recipients ?? [];
      return {
        data: convertRows(recipients),
        total: body.outboundCount ?? 0,
        fields: body.fields ?? [],
      };
    });
  },

  previewFmbOutstandingRecipients: (
    resource: string,
    params: {
      hijriYearStart?: number;
      minOutstandingPercent?: number;
      thaliTypeIds?: string[];
      deliveryScheduleProfileIds?: string[];
      itsIds?: string[];
      notifyMode?: string;
      filterCriteria?: unknown;
      limit?: number;
      offset?: number;
    }
  ) => {
    const {
      hijriYearStart,
      minOutstandingPercent,
      thaliTypeIds,
      deliveryScheduleProfileIds,
      itsIds,
      notifyMode,
      filterCriteria,
      limit = 10000,
      offset = 0,
    } = params;
    return httpClient(`${getApiUrl(resource)}/${resource}/preview-fmb-outstanding-recipients`, {
      method: "POST",
      body: JSON.stringify({
        hijriYearStart,
        minOutstandingPercent,
        thaliTypeIds,
        deliveryScheduleProfileIds,
        itsIds,
        notifyMode,
        filterCriteria,
        limit,
        offset,
      }),
    }).then(({ json }) => {
      const body = json as {
        recipients?: unknown[];
        outboundCount?: number;
        hijriYearStart?: number;
        notifyMode?: string;
        fields?: unknown[];
      };
      const recipients = body.recipients ?? [];
      return {
        data: convertRows(recipients),
        total: body.outboundCount ?? 0,
        hijriYearStart: body.hijriYearStart,
        notifyMode: body.notifyMode,
        fields: body.fields ?? [],
      };
    });
  },

  previewSabilOutstandingRecipients: (
    resource: string,
    params: {
      minOutstandingPercent?: number;
      itsIds?: string[];
      excludeItsIds?: string[];
      notifyMode?: string;
      sabilType?: string;
      filterCriteria?: unknown;
      limit?: number;
      offset?: number;
    }
  ) => {
    const {
      minOutstandingPercent,
      itsIds,
      excludeItsIds,
      notifyMode,
      sabilType,
      filterCriteria,
      limit = 10000,
      offset = 0,
    } = params;
    return httpClient(`${getApiUrl(resource)}/${resource}/preview-sabil-outstanding-recipients`, {
      method: "POST",
      body: JSON.stringify({
        minOutstandingPercent,
        itsIds,
        excludeItsIds,
        notifyMode,
        sabilType,
        filterCriteria,
        limit,
        offset,
      }),
    }).then(({ json }) => {
      const body = json as {
        recipients?: unknown[];
        outboundCount?: number;
        notifyMode?: string;
        asOfDate?: string;
        fields?: unknown[];
      };
      const recipients = body.recipients ?? [];
      return {
        data: convertRows(recipients),
        total: body.outboundCount ?? 0,
        notifyMode: body.notifyMode,
        asOfDate: body.asOfDate,
        fields: body.fields ?? [],
      };
    });
  },

  previewMuttavatteenOutstandingRecipients: (
    resource: string,
    params: {
      minOutstandingPercent?: number;
      itsIds?: string[];
      excludeItsIds?: string[];
      notifyMode?: string;
      sabilType?: string;
      filterCriteria?: unknown;
      limit?: number;
      offset?: number;
    }
  ) => {
    const {
      minOutstandingPercent,
      itsIds,
      excludeItsIds,
      notifyMode,
      sabilType,
      filterCriteria,
      limit = 10000,
      offset = 0,
    } = params;
    return httpClient(
      `${getApiUrl(resource)}/${resource}/preview-muttavatteen-outstanding-recipients`,
      {
        method: "POST",
        body: JSON.stringify({
          minOutstandingPercent,
          itsIds,
          excludeItsIds,
          notifyMode,
          sabilType,
          filterCriteria,
          limit,
          offset,
        }),
      }
    ).then(({ json }) => {
      const body = json as {
        recipients?: unknown[];
        outboundCount?: number;
        notifyMode?: string;
        asOfDate?: string;
        fields?: unknown[];
      };
      const recipients = body.recipients ?? [];
      return {
        data: convertRows(recipients),
        total: body.outboundCount ?? 0,
        notifyMode: body.notifyMode,
        asOfDate: body.asOfDate,
        fields: body.fields ?? [],
      };
    });
  },

  lookupIts: (resource: string, params: { itsIds?: unknown }) =>
    httpClient(`${getApiUrl(resource)}/${resource}/lookup-its`, {
      method: "POST",
      body: JSON.stringify({ itsIds: params.itsIds }),
    }).then(({ json }) => {
      const body = json as {
        matched?: unknown[];
        unmatched?: unknown[];
        count?: number;
      };
      return {
        matched: body.matched ?? [],
        unmatched: body.unmatched ?? [],
        count: body.count ?? 0,
      };
    }),
} as DataProvider;

export default dataProvider;
